/**
 * Google Analytics tracking for THPT app
 * Re-exports shared tracking + adds THPT-specific CAT events
 */

// Re-export all shared tracking functions
export {
  trackGAPageView,
  trackGAEvent,
  setGAUserId,
  trackSignUp,
  trackLogin,
  trackExamView,
  trackExamStart,
  trackExamSubmit,
  trackExamResult,
  trackExamExit,
  trackExamResume,
  trackExamPurchase,
  trackExamBeginCheckout,
  trackExamSearch,
  trackExamFilter,
  trackCTAClick,
  trackScrollDepth,
  trackOutboundClick,
  trackShare,
  initAutoPageViewTracking,
  initScrollDepthTracking,
} from '@vsat/shared/telemetry/googleAnalytics';

// Re-export shared types
export type {
  ExamViewEvent,
  ExamStartEvent,
  ExamSubmitEvent,
  ExamResultEvent,
  ExamSearchEvent,
  ExamFilterEvent,
  ExamPurchaseEvent,
} from '@vsat/shared/telemetry/googleAnalytics';

import { trackGAEvent } from '@vsat/shared/telemetry/googleAnalytics';

// ============================================
// THPT-SPECIFIC: CAT CONVERSION FUNNEL
// ============================================

export interface CATStartEvent {
  education_level?: 'THPT' | 'THCS';
}

export interface CATProgressEvent {
  group_index: number;
  group_name: string;
  total_groups: number;
}

export interface CATCompleteEvent {
  total_questions: number;
  duration_seconds?: number;
}

export interface ViewResultEvent {
  top_groups: string[];
  session_id?: string;
}

export interface BeginCheckoutEvent {
  value: number;
  currency: string;
  session_id?: string;
}

export interface PurchaseEvent {
  transaction_id: string;
  value: number;
  currency: string;
  session_id?: string;
}

export interface DownloadReportEvent {
  format: 'pdf' | 'image';
  session_id?: string;
}

export function trackCATStart(params?: CATStartEvent) {
  trackGAEvent('cat_start', {
    education_level: params?.education_level,
  });
}

export function trackCATProgress(params: CATProgressEvent) {
  trackGAEvent('cat_progress', {
    group_index: params.group_index,
    group_name: params.group_name,
    total_groups: params.total_groups,
    progress_percent: Math.round((params.group_index / params.total_groups) * 100),
  });
}

export function trackCATComplete(params: CATCompleteEvent) {
  trackGAEvent('cat_complete', {
    total_questions: params.total_questions,
    duration_seconds: params.duration_seconds,
  });
}

export function trackViewFreeResult(params: ViewResultEvent) {
  trackGAEvent('view_free_result', {
    top_groups: params.top_groups.join(','),
    session_id: params.session_id,
  });
}

export function trackBeginCheckout(params: BeginCheckoutEvent) {
  trackGAEvent('begin_checkout', {
    currency: params.currency,
    value: params.value,
    session_id: params.session_id,
    items: [
      {
        item_id: 'cat_full_report',
        item_name: 'Báo cáo chi tiết CAT',
        price: params.value,
        quantity: 1,
      },
    ],
  });
}

export function trackPurchase(params: PurchaseEvent) {
  trackGAEvent('purchase', {
    transaction_id: params.transaction_id,
    currency: params.currency,
    value: params.value,
    session_id: params.session_id,
    items: [
      {
        item_id: 'cat_full_report',
        item_name: 'Báo cáo chi tiết CAT',
        price: params.value,
        quantity: 1,
      },
    ],
  });
}

export function trackViewFullReport(sessionId?: string) {
  trackGAEvent('view_full_report', {
    session_id: sessionId,
  });
}

export function trackDownloadReport(params: DownloadReportEvent) {
  trackGAEvent('download_report', {
    format: params.format,
    session_id: params.session_id,
  });
}

export function trackSendReportEmail(sessionId?: string) {
  trackGAEvent('send_report_email', {
    session_id: sessionId,
  });
}
